// ============================================================================
// Beacon — place-based eligibility (QCT / DDA / Opportunity Zone / USDA rural)
//
// These federal designations are NOT one-time approvals — they are per-address
// facts looked up against public datasets, keyed off the property's census
// tract. This module is pure/deterministic like the engine; the one async bit
// (`resolveTract`) hits the free US Census Geocoder to turn an address into the
// tract GEOID that every one of these designations is indexed by.
//
// NOTE on Florida "Enterprise Zones": that state program EXPIRED 12/31/2015 and
// was never renewed. The overlays below are what actually drive affordable-
// housing economics today. See beacon-build-plan for the Phase-2 live feeds.
// ============================================================================

import type { Deal, Eligibility, Metrics, Overlay } from "../types";

// Official verification maps (address is confirmed by the user on the tool).
export const VERIFY = {
  // HUD SADDA map serves BOTH Qualified Census Tracts and Difficult Dev. Areas.
  qctDda: "https://www.huduser.gov/portal/sadda/sadda_qct.html",
  // HUD Opportunity Zones locator (CDFI Fund is the underlying designation).
  oz: "https://opportunityzones.hud.gov/",
  // USDA Rural Development property eligibility — the "rural America" map.
  usda: "https://eligibility.sc.egov.usda.gov/eligibility/welcomeAction.do",
} as const;

/**
 * Build the full eligibility picture for a deal. Pure — safe to call on every
 * render and reuse server-side for reports.
 */
export function eligibility(d: Deal, m: Metrics): Eligibility {
  const qct = d.qct ?? "unknown";
  const dda = d.dda ?? "unknown";
  const usdaRural = d.usdaRural ?? "unknown";
  const ozStatus = d.oz ? "yes" : "unknown"; // `oz` is a plain boolean elsewhere

  const boostEligible = qct === "yes" || dda === "yes";
  // Illustrative: the 30% boost applies to eligible basis (rehab + soft costs
  // are the cleanest proxy here). Only relevant if LIHTC is layered on.
  const basisBoost = boostEligible ? 0.3 * (d.rehab + d.soft) : 0;

  const overlays: Overlay[] = [
    {
      key: "qct",
      label: "QCT",
      full: "Qualified Census Tract",
      status: qct,
      what: "A HUD-designated census tract with high poverty or a large share of low-income households.",
      why: "If you layer LIHTC on the deal, a QCT gives a 30% boost to eligible basis — materially more tax credits for the same building.",
      impactLabel: "Extra LIHTC eligible basis (30% boost)",
      impact: qct === "yes" ? 0.3 * (d.rehab + d.soft) : undefined,
      verifyUrl: VERIFY.qctDda,
      source: "HUD User — SADDA",
    },
    {
      key: "dda",
      label: "DDA",
      full: "Difficult Development Area",
      status: dda,
      what: "A HUD-designated high-cost area where construction/land/utility costs are high relative to incomes.",
      why: "Same 30% basis boost as a QCT — a property only needs to be in one of the two to qualify for the boost.",
      impactLabel: "Extra LIHTC eligible basis (30% boost)",
      impact: dda === "yes" ? 0.3 * (d.rehab + d.soft) : undefined,
      verifyUrl: VERIFY.qctDda,
      source: "HUD User — SADDA",
    },
    {
      key: "oz",
      label: "OZ",
      full: "Opportunity Zone",
      status: ozStatus,
      what: "A tract where investors can defer/reduce capital-gains tax by rolling gains into the project. FL has 427; 'OZ 2.0' (2025) extends the program.",
      why: "Lets your LP investors shelter capital gains — a real fundraising edge. The current designation is time-limited, so it belongs in the offering terms.",
      verifyUrl: VERIFY.oz,
      source: "HUD / CDFI Fund",
    },
    {
      key: "usda",
      label: "USDA rural",
      full: "USDA Rural-Eligible Area",
      status: usdaRural,
      what: "USDA's rural determination for a specific address (~97% of US land area qualifies). This is the 'put an address on a map' rural check.",
      why: "Rural-eligible unlocks USDA Rural Development multifamily financing (Section 515/538) — the same rural map also governs USDA Community Facilities (rural healthcare) loans.",
      verifyUrl: VERIFY.usda,
      source: "USDA Rural Development",
    },
  ];

  return {
    overlays,
    boostEligible,
    basisBoost,
    ruralPath: usdaRural === "yes",
  };
}

// ---------------------------------------------------------------------------
// Address -> census tract (free, keyless US Census Geocoder).
// Every designation above is indexed by this GEOID, so resolving it is the
// first step of any real verification.
// ---------------------------------------------------------------------------
export interface TractResult {
  geoid: string; // 11-digit tract GEOID
  tract: string; // 6-digit tract code
  county: string;
  state: string;
  matchedAddress: string;
  lat?: number; // point (for parcel / flood lookups)
  lng?: number;
}

// Same-origin path. In dev, Vite proxies "/geo" -> the Census geocoder (which
// sends no CORS headers of its own); in cloud/prod a Supabase Edge Function or
// Worker serves the same path. Keeping it relative means the browser never
// makes a cross-origin request, so it isn't CORS-blocked.
export function censusGeocoderUrl(addr: string): string {
  const q = encodeURIComponent(addr);
  return (
    "/geo/geographies/onelineaddress" +
    `?address=${q}&benchmark=Public_AR_Current&vintage=Current_Current&format=json`
  );
}

/** The absolute upstream URL — for reference / server-side proxies. */
export function censusGeocoderDirectUrl(addr: string): string {
  return censusGeocoderUrl(addr).replace("/geo/", "https://geocoding.geo.census.gov/geocoder/");
}

/**
 * Resolve a street address to its census tract via the US Census Geocoder.
 * Best-effort: throws on network/CORS failure so the UI can fall back to a
 * "verify on the official map" link. No API key required.
 */
export async function resolveTract(addr: string): Promise<TractResult | null> {
  const res = await fetch(censusGeocoderUrl(addr));
  if (!res.ok) throw new Error(`Geocoder HTTP ${res.status}`);
  const data = await res.json();
  const match = data?.result?.addressMatches?.[0];
  if (!match) return null;
  const tracts = match.geographies?.["Census Tracts"];
  const t = tracts?.[0];
  if (!t) return null;
  const c = match.coordinates; // { x: lng, y: lat }
  return {
    geoid: t.GEOID,
    tract: t.TRACT,
    county: t.COUNTY,
    state: t.STATE,
    matchedAddress: match.matchedAddress ?? addr,
    lat: typeof c?.y === "number" ? c.y : undefined,
    lng: typeof c?.x === "number" ? c.x : undefined,
  };
}
